<script setup lang="ts">
import { onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useProjectStore } from '@/stores/project'
import { useProjectUserStore } from '@/stores/projectUser'
import { useAuthorizationStore } from '@/stores/authorization'
import { useDjangoAuthStore } from '@/stores/authDjango'
import { storeToRefs } from 'pinia'
import _ from 'lodash'
import ProjectSideBar from '@/components/project-detail/ProjectSideBar.vue'
import { AppBackground, ProjectHeaderImage } from '@haskoning/vue-component-library'
import type { ProjectUserType } from '@/stores/type/projectUser.type'
import router from '@/router'

const route = useRoute()

const projectStore = useProjectStore()
const authStore = useDjangoAuthStore()
const projectUserStore = useProjectUserStore()
const authorizationStore = useAuthorizationStore()

const { currentItem: currentProjectItem } = storeToRefs(projectStore)
const { items: projectUsers } = storeToRefs(projectUserStore)
const { user: currentUser } = storeToRefs(authStore)
const { items: roles } = storeToRefs(authorizationStore)

const applyCurrentProjectRole = () => {
  if (!currentUser.value?.id) return
  if (!roles.value?.length) return

  const currentProjectUser = projectUsers.value.find(
    (item: ProjectUserType) => item.user === currentUser.value?.id
  )
  if (!currentProjectUser?.role) return

  authorizationStore.currentRoles = []
  authorizationStore.setCurrentRole([currentProjectUser.role])
}

onMounted(async () => {
  if (!currentUser.value) {
    await authStore.reAuthenticate()
  }

  await projectStore.fetchOptions()
  const projectId = String(route.params.id ?? '')
  await projectStore.fetchItem(parseInt(projectId))

  if (!currentProjectItem.value || _.isEmpty(currentProjectItem.value)) {
    await router.push({ name: 'not-found' })
  }

  if (currentProjectItem.value) {
    const { id } = currentProjectItem.value

    if (id) {
      // Fetch authorization and project users in parallel
      await Promise.all([
        authorizationStore.fetchItems(),
        projectUserStore.fetchItems({ search: id })
      ])
      applyCurrentProjectRole()
    }
  }
})

watch([currentUser, projectUsers, roles], () => {
  applyCurrentProjectRole()
})

onUnmounted(() => {
  projectStore.resetAll()
})
</script>

<template>
  <div style="display: flex; flex-direction: column" class="fill-height">
    <ProjectHeaderImage
      class="project-header-image"
      :lazy-src="'/header_lazy.jpg'"
      :src="'/header.jpeg'"
    />
    <AppBackground class="px-8 py-4 fill-height">
      <ProjectSideBar />
      <router-view class="fill-height"></router-view>
    </AppBackground>
  </div>
</template>

<style scoped></style>
